import {
  Component,
  ElementRef,
  Input,
  OnInit,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { ColorService } from '@app/shared/services/color.service';
import { HighchartsService } from '@app/shared/services/highcharts.service';
import { TrackerService } from '@app/shared/services/tracker.service';
import { TranslateService } from '@ngx-translate/core';
import { TagsApi } from '@app/tags/tag-api';
import { TagSignalCount } from '@app/profile/profile-api';
import { Chart } from 'highcharts';
import { SanitizationService } from '@app/shared/services/sanitization.service';

interface ChartData {
  name: string;
  y: number;
  hasSignals: boolean;
  color: string;
}

@Component({
  selector: 'dgx-signal-count-chart-v2',
  templateUrl: './signal-count-chart.component.html',
  styleUrls: ['./signal-count-chart.component.scss'],
})
export class SignalCountChartComponentV2 implements OnInit, OnChanges {
  @Input() public signalCounts: TagSignalCount[];
  @Input() public skills: TagsApi.Tag[];
  @Input() public currentSkill: string;
  @Input() public maxSkills: number;

  public chart: Chart;

  private colorList = ['orange', 'pink', 'ocean', 'forest', 'purple', 'sky'];
  public chartColors = this.colorList.map((c) => this.colors.getColor(c));

  public i18n = this.translate.instant([
    'Core_NoResults',
    'dgProfileOverview_SkillSignalsTitle',
    'dgProfileOverview_TooltipSignalsPlural',
    'dgProfileOverview_TooltipSignalsSingle',
  ]);
  constructor(
    private element: ElementRef,
    private highChartsService: HighchartsService,
    private colors: ColorService,
    private tracker: TrackerService,
    private translate: TranslateService,
    private sanitizationService: SanitizationService
  ) {}

  public ngOnInit(): void {
    this.generateChart();
  }
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.signalHistories || changes.skills) {
      this.generateChart();
    }

    const hasCurrentSkillChanged =
      changes?.currentSkill?.currentValue !==
      changes?.currentSkill?.previousValue;
    if (hasCurrentSkillChanged) {
      this.selectPoint(this.currentSkill);
    }
  }

  public hasSignalsForSkill(skillName: string): boolean {
    const skillId = this.getSkillId(skillName);
    return !!this.signalCounts.find((count) => count.tagId === skillId);
  }

  public getSkillId(skillName: string): number {
    return this.skills.find((s) => s.title === skillName).tagId;
  }

  public selectPoint(skillName: string): void {
    if (skillName && this.hasSignalsForSkill(skillName)) {
      this.chart?.series[0].data.forEach((p) => {
        if (p.name === skillName) {
          p.setState('hover');
        } else {
          p.setState('inactive');
        }
      });
      this.chart.setTitle({ text: this.getChartTitle(skillName) });
    } else {
      this.chart?.series[0].data.forEach((p) => {
        p.setState('normal');
      });
      this.chart.tooltip.hide(0);
      this.chart.setTitle({ text: this.getChartTitle() });
    }
  }

  public getChartData(): ChartData[] {
    const chartData = this.skills
      .slice(0, this.maxSkills)
      .reduce((chartData, s, index) => {
        if (this.hasSignalsForSkill(s.title)) {
          chartData.push({
            name: this.sanitizationService.cleanHtml(s.title),
            y: this.signalCounts.find((c) => c.tagId === s.tagId).count,
            hasSignals: true,
            color: this.chartColors[index],
          });
        }
        return chartData;
      }, []);

    if (!chartData.length) {
      return this.skills.slice(0, this.maxSkills).map((s, index) => ({
        name: this.sanitizationService.cleanHtml(s.title),
        y: 1,
        hasSignals: false,
        color: this.chartColors[index],
      }));
    }
    return chartData;
  }

  public getChartTitle(skillName: string = ''): string {
    let signalTotal = this.skills
      .slice(0, this.maxSkills)
      .reduce((total, s) => {
        const signalCount = this.signalCounts.find((c) => c.tagId === s.tagId);
        if (signalCount) {
          return total + signalCount.count;
        }
        return total;
      }, 0);
    if (skillName) {
      const skillId = this.getSkillId(skillName);
      signalTotal = this.signalCounts.find((count) => count.tagId === skillId)
        .count;
    }

    return `
      <h2 class="h2 center-text">${signalTotal}</h2>
      <h2 class="center-text par--small par--light_a">${this.i18n.dgProfileOverview_SkillSignalsTitle}</h2>
    `;
  }

  public generateChart(): void {
    const signalTotal = this.signalCounts.reduce(
      (total, s) => total + s.count,
      0
    );
    const tooltipTitlePlural = this.i18n.dgProfileOverview_TooltipSignalsPlural;
    const tooltipTitleSingle = this.i18n.dgProfileOverview_TooltipSignalsSingle;

    const container = this.element.nativeElement.querySelector(
      '.highcharts-donut-signals'
    );
    const chartData = this.getChartData();

    this.highChartsService
      .createChart(container, {
        title: {
          useHTML: true,
          text: this.getChartTitle(),
          verticalAlign: 'middle',
          style: {
            zIndex: 0,
          },
        },
        chart: {
          style: {
            fontFamily: 'inherit',
          },
          backgroundColor: 'transparent',
          margin: [0, 0, 0, 0],
        },
        plotOptions: {
          pie: {
            dataLabels: {
              enabled: false,
            },
            showInLegend: false,
          },
        },
        tooltip: {
          useHTML: true,
          backgroundColor: this.colors.getColor('white'),
          borderWidth: 1,
          style: {
            padding: 0,
            zIndex: 999,
          },
          outside: true,
          formatter: function () {
            const hasSignals = this.point.options.hasSignals;
            const tooltipTitle =
              this.point.y === 1 && hasSignals
                ? tooltipTitleSingle
                : tooltipTitlePlural;

            const signalCount =
              signalTotal === 0 || !hasSignals ? 0 : this.point.y;

            return `
                <div class="highcharts__tooltip guts-p-v-half guts-p-h-2">
                    <div class="break">
                      ${this.key}
                    </div>
                    <div class="text-center">
                      <span>${signalCount} ${tooltipTitle}</span>
                    </div>
                </div>
            `;
          },
        },
        credits: {
          enabled: false,
        },
        series: [
          {
            type: 'pie',
            innerSize: '65%',
            data: chartData,
          },
        ],
      })
      .subscribe((chart) => (this.chart = chart));
  }
}
